import * as R from 'ramda';

import { getEnrichedHierarchyLevels } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/hierarchy';
import { getIssueTypeIdsOfCompanyManagedProjects } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/issue-types';
import { getFilteredIssues } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/issues';
import type { EnrichedHierarchyLevel } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/hierarchy/types';
import type { Issue } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/issues/types';
import { createSelector } from '@atlassian/jira-portfolio-3-portfolio/src/common/reselect';
import type { MapStateToProps } from '@atlassian/jira-portfolio-3-portfolio/src/common/types/redux';
import type { IssueTypeLegendItem, StateProps } from './types';

export const getIssueTypeLegendItemsPure = (
	enrichedHierarchyLevels: EnrichedHierarchyLevel[],
	issueTypeIdsOfCompanyManagedProjects: number[],
	filteredIssues: Issue[],
): IssueTypeLegendItem[] => {
	const visibleIssueTypes = new Set(filteredIssues.map((issue) => issue.type));

	// Sort issue types by hierarchy level descending and filter to only the visible issue types
	const sortedLevels = R.sort(R.descend(R.prop('level')), enrichedHierarchyLevels);
	const issueTypes = sortedLevels
		.flatMap((level) => level.issueTypes)
		.filter((issueType) => visibleIssueTypes.has(issueType.id));

	// Split the sorted issue types by company managed or team managed
	const [companyManagedIssueTypes, teamManagedIssueTypes] = R.partition(
		(issueType) => issueTypeIdsOfCompanyManagedProjects.includes(issueType.id),
		issueTypes,
	);

	// Create mapping function to group issue types by name and remove any duplicated icons
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const issueTypeIconsGroupByName: Record<string, any> = {};
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const mapIssueTypeNameWithIconUrl = (issueType: any) => {
		if (!issueTypeIconsGroupByName[issueType.name]) {
			issueTypeIconsGroupByName[issueType.name] = [issueType.iconUrl];
		} else if (!issueTypeIconsGroupByName[issueType.name].includes(issueType.iconUrl)) {
			issueTypeIconsGroupByName[issueType.name].push(issueType.iconUrl);
		}
	};

	// Run the mapping function on the company managed issue types first
	// then on the team managed ones
	companyManagedIssueTypes.forEach(mapIssueTypeNameWithIconUrl);
	teamManagedIssueTypes.forEach(mapIssueTypeNameWithIconUrl);

	// Construct issue type legend items
	const issueTypeLegendItems: Array<IssueTypeLegendItem> = [];
	for (const name in issueTypeIconsGroupByName) {
		if (issueTypeIconsGroupByName[name]) {
			issueTypeLegendItems.push({
				name,
				iconUrls: issueTypeIconsGroupByName[name],
			});
		}
	}
	return issueTypeLegendItems;
};

const getIssueTypeLegendItems = createSelector(
	[getEnrichedHierarchyLevels, getIssueTypeIdsOfCompanyManagedProjects, getFilteredIssues],
	getIssueTypeLegendItemsPure,
);

export const mapStateToProps: MapStateToProps<StateProps> = (state) => ({
	issueTypeLegendItems: getIssueTypeLegendItems(state),
});
